import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { discoveryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Heart, MessageCircle, Activity, User, Settings, CheckCircle } from 'lucide-react';

export const Matches = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [likesReceived, setLikesReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('matches');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [matchesRes, likesRes] = await Promise.all([
        discoveryAPI.getMatches(),
        discoveryAPI.getLikesReceived()
      ]);
      setMatches(matchesRes.data);
      setLikesReceived(likesRes.data);
    } catch (error) {
      console.error('Failed to load matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const levelColors = {
    beginner: 'bg-green-500',
    intermediate: 'bg-blue-500',
    advanced: 'bg-yellow-500',
    elite: 'bg-orange-500',
    college_pro: 'bg-red-500'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3">
          <h1 className="font-display font-bold text-xl uppercase text-center text-gray-900">Matches</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="fixed top-14 left-0 right-0 z-20 bg-white border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 flex">
          <button
            data-testid="matches-tab"
            className={`flex-1 py-3 font-body text-sm border-b-2 transition-colors ${
              activeTab === 'matches' ? 'border-primary text-primary font-medium' : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('matches')}
          >
            Matches ({matches.length})
          </button>
          <button
            data-testid="likes-tab"
            className={`flex-1 py-3 font-body text-sm border-b-2 transition-colors ${
              activeTab === 'likes' ? 'border-primary text-primary font-medium' : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('likes')}
          >
            Likes Received ({likesReceived.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="pt-28 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          {activeTab === 'matches' ? (
            matches.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {matches.map((match) => (
                  <Card 
                    key={match.match_id}
                    data-testid={`match-card-${match.match_id}`}
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow bg-white border-gray-100"
                    onClick={() => navigate(`/messages/${match.match_id}`)}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={match.photos?.[0] || 'https://via.placeholder.com/200'}
                        alt={match.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <div className="flex items-center gap-1">
                          <h3 className="font-display font-bold text-white text-lg">{match.name}</h3>
                          {match.selfie_verified && (
                            <CheckCircle className="w-4 h-4 text-blue-400" />
                          )}
                        </div>
                        {match.sports?.[0] && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className={`w-2 h-2 rounded-full ${levelColors[match.sports[0].level]}`} />
                            <span className="text-white/80 text-xs font-body">{match.sports[0].sport}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Heart className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h2 className="font-display text-xl uppercase text-gray-900 mb-2">No Matches Yet</h2>
                <p className="font-body text-gray-500 mb-6">Keep swiping to find your perfect match!</p>
                <Button onClick={() => navigate('/discover')}>
                  Find Matches
                </Button>
              </div>
            )
          ) : (
            likesReceived.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {likesReceived.map((like, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow bg-white border-gray-100"
                    onClick={() => navigate('/discover')}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={like.photos?.[0] || 'https://via.placeholder.com/200'}
                        alt={like.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="font-display font-bold text-white text-lg">{like.name}</h3>
                        <p className="text-white/80 text-xs font-body">Liked you!</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Heart className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h2 className="font-display text-xl uppercase text-gray-900 mb-2">No Likes Yet</h2>
                <p className="font-body text-gray-500">Complete your profile to attract more likes!</p>
              </div>
            )
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-2 flex justify-around">
          <button onClick={() => navigate('/discover')} className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Activity className="w-6 h-6" />
            <span className="text-xs font-body">Discover</span>
          </button>
          <button className="flex flex-col items-center gap-1 p-2 text-primary">
            <Heart className="w-6 h-6" />
            <span className="text-xs font-body">Matches</span>
          </button>
          <button onClick={() => navigate('/messages')} className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <MessageCircle className="w-6 h-6" />
            <span className="text-xs font-body">Messages</span>
          </button>
          <button onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User className="w-6 h-6" />
            <span className="text-xs font-body">Profile</span>
          </button>
          <button onClick={() => navigate('/settings')} className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Settings className="w-6 h-6" />
            <span className="text-xs font-body">Settings</span>
          </button>
        </div>
      </nav>
    </div>
  );
};
